/**
 * Tic-Tac-Toe AI using Minimax Algorithm
 * Creates an unbeatable AI opponent
 */

type Player = 'X' | 'O' | null
type MiniBoard = Player[]
type GameBoard = MiniBoard[]

interface Move {
  boardIndex: number
  cellIndex: number
  score: number
}

/**
 * Check if a board (mini or main) has a winner
 */
function checkWinner(cells: Player[]): Player {
  const lines = [
    [0, 1, 2], [3, 4, 5], [6, 7, 8], // rows
    [0, 3, 6], [1, 4, 7], [2, 5, 8], // columns
    [0, 4, 8], [2, 4, 6], // diagonals
  ]

  for (const [a, b, c] of lines) {
    if (cells[a] && cells[a] === cells[b] && cells[a] === cells[c]) {
      return cells[a]
    }
  }
  return null
}

/**
 * Get available moves in a mini board
 */
function getAvailableCells(board: Player[]): number[] {
  return board.map((cell, i) => (cell === null ? i : -1)).filter(i => i !== -1)
}

/**
 * Minimax algorithm for Ultimate Tic-Tac-Toe
 * Returns best move with score
 */
function minimax(
  board: GameBoard,
  miniWinners: Player[],
  currentPlayer: Player,
  nextBoard: number | null,
  depth: number,
  isMaximizing: boolean,
  aiSymbol: Player,
  maxDepth: number = 3 // Limit depth for performance
): Move {
  const opponent: Player = aiSymbol === 'X' ? 'O' : 'X'

  // Check if game is over
  const mainWinner = checkWinner(miniWinners)
  if (mainWinner === aiSymbol) return { boardIndex: -1, cellIndex: -1, score: 100 - depth }
  if (mainWinner === opponent) return { boardIndex: -1, cellIndex: -1, score: -100 + depth }
  if (depth >= maxDepth) return { boardIndex: -1, cellIndex: -1, score: 0 } // Depth limit

  const availableBoards = nextBoard !== null
    ? (miniWinners[nextBoard] === null ? [nextBoard] : [])
    : miniWinners.map((w, i) => (w === null ? i : -1)).filter(i => i !== -1)

  if (availableBoards.length === 0) {
    return { boardIndex: -1, cellIndex: -1, score: 0 } // Draw
  }

  let bestMove: Move = { boardIndex: -1, cellIndex: -1, score: isMaximizing ? -Infinity : Infinity }

  for (const boardIdx of availableBoards) {
    const availableCells = getAvailableCells(board[boardIdx])

    for (const cellIdx of availableCells) {
      // Make move
      const newBoard = board.map((b, i) =>
        i === boardIdx ? b.map((c, j) => (j === cellIdx ? currentPlayer : c)) : [...b]
      )
      const newMiniWinners = [...miniWinners]
      const miniBoardWinner = checkWinner(newBoard[boardIdx])
      if (miniBoardWinner) {
        newMiniWinners[boardIdx] = miniBoardWinner
      }

      // Fixed: check if target board is won or full before setting nextBoard
      const isBoardFull = (cells: Player[]) => cells.every(cell => cell !== null)
      const nextBoardIdx = (newMiniWinners[cellIdx] === null && !isBoardFull(newBoard[cellIdx])) ? cellIdx : null

      // Recursively evaluate
      const result = minimax(
        newBoard,
        newMiniWinners,
        isMaximizing ? opponent : aiSymbol,
        nextBoardIdx,
        depth + 1,
        !isMaximizing,
        aiSymbol,
        maxDepth
      )

      if (isMaximizing) {
        if (result.score > bestMove.score) {
          bestMove = { boardIndex: boardIdx, cellIndex: cellIdx, score: result.score }
        }
      } else {
        if (result.score < bestMove.score) {
          bestMove = { boardIndex: boardIdx, cellIndex: cellIdx, score: result.score }
        }
      }
    }
  }

  return bestMove
}

/**
 * Get AI move for Ultimate Tic-Tac-Toe
 */
export function getTicTacToeAIMove(
  board: GameBoard,
  miniWinners: Player[],
  nextBoard: number | null,
  aiSymbol: 'X' | 'O',
  difficulty: 'easy' | 'medium' | 'hard' = 'hard'
): { boardIndex: number; cellIndex: number } {
  const maxDepth = difficulty === 'easy' ? 1 : difficulty === 'medium' ? 2 : 3

  // Easy mode: Random move
  if (difficulty === 'easy' && Math.random() < 0.5) {
    const availableBoards = nextBoard !== null
      ? (miniWinners[nextBoard] === null ? [nextBoard] : [])
      : miniWinners.map((w, i) => (w === null ? i : -1)).filter(i => i !== -1)

    if (availableBoards.length > 0) {
      const randomBoard = availableBoards[Math.floor(Math.random() * availableBoards.length)]
      const availableCells = getAvailableCells(board[randomBoard])
      const randomCell = availableCells[Math.floor(Math.random() * availableCells.length)]
      return { boardIndex: randomBoard, cellIndex: randomCell }
    }
  }

  // Use minimax for medium/hard or when easy fails
  const move = minimax(board, miniWinners, aiSymbol, nextBoard, 0, true, aiSymbol, maxDepth)

  if (move.boardIndex === -1) {
    // Fallback to first available move
    const availableBoards = nextBoard !== null
      ? (miniWinners[nextBoard] === null ? [nextBoard] : [])
      : miniWinners.map((w, i) => (w === null ? i : -1)).filter(i => i !== -1)

    const randomBoard = availableBoards[0]
    const availableCells = getAvailableCells(board[randomBoard])
    return { boardIndex: randomBoard, cellIndex: availableCells[0] }
  }

  return { boardIndex: move.boardIndex, cellIndex: move.cellIndex }
}
